<template>
  <div class="game-view-container">
    <DemoWaitView
      ref="waitViewInstance"
      :gameRoomcode="gameRoomcode"
      :gameState="gameState"
      :isHost="isHost"
      v-if="displayWaitView"
      @update-game-state="propagateEvent('update-game-state', $event)"
    />
    <transition name="fade-slow">
      <PromptTimerAnimation
        v-if="displayQuestionView"
        :duration="totalSecondsRemaining"
      />
    </transition>
    <transition name="fade">
      <div
        class="question-container"
        v-if="displayQuestionView && totalSecondsRemaining > 0"
      >
        <div class="header-info-container">
          <transition name='fade-slow'>
            <div
              v-if="activeQuestionIndex < gameState.prompts.length && timeRemaining.length > 0"
              class="prompt-index-text"
            >
              Prompt {{ activeQuestionIndex + 1 }} of {{ gameState.prompts.length }}
            </div>
          </transition>
          <transition name='fade'>
            <div
              v-if="timeRemaining.length > 0"
              :class="[ 'timer-text', { 'thirty-seconds-left': totalSecondsRemaining < 31 && totalSecondsRemaining >= 11, 'ten-seconds-left': totalSecondsRemaining < 11 && totalSecondsRemaining >= 0, 'zero-seconds-left': totalSecondsRemaining < 0 }]"
            >
              {{ timeRemaining }}
            </div>
          </transition>
        </div>
        <transition-group name="pop-from-top-delay">
          <DemoQuestionView
            v-for="(question, questionIndex) in gameState.prompts"
            v-show="questionIndex === activeQuestionIndex"
            @answer-submitted="submitAnswer"
            :key="question.promptText"
            :username="username"
            :questionIndex="questionIndex"
            :numQuestions="gameState.prompts.length"
            :gameState="gameState"
            :gameRoomcode="gameRoomcode"
            :questionData="question"
            :isSafari="isSafari"
            class="question-view-item"
          />
        </transition-group>
      </div>
    </transition>
    <transition name="pop-from-top-delay">
      <div
        class="question-container"
        v-if="displayQuestionView && isAnswerSubmissionComplete"
      >
        <div
          class="question-view-item"
        >
          <div
            class="submission-message-container"
          >
            <div>
              You're all set!
            </div>
            <div>
              Waiting for other players to submit their answers...
            </div>
          </div>
        </div>
      </div>
    </transition>
    <transition name="pop-from-top">
      <VoteStartView
        ref="voteStartViewInstance"
        v-if="displayVoteStartView"
      />
    </transition>
    <DemoVoteView
      ref="voteViewInstance"
      v-if="displayVoteView"
      :voteInfo="turnInfo"
      :primaryColor="primaryColor"
      :totalSecondsRemaining="totalSecondsRemaining"
      :timeRemaining="timeRemaining"
      :isSafari="isSafari"
      @vote-submitted="submitVote"
      @play-sound-effect="propagateEvent('play-sound-effect', $event)"
    />
    <transition name="pop-from-top">
      <VoteResultView
        ref="voteResultViewInstance"
        v-if="displayVoteResultView"
        :voteResultInfo="previousTurnResult"
        :isSafari="isSafari"
        @play-sound-effect="propagateEvent('play-sound-effect', $event)"
      />
    </transition>
    <RoundResultView
      ref="roundResultViewInstance"
      v-show="displayRoundResultView"
      :isLastRound="isLastRound"
    />
    <GameEndView
      ref="gameEndViewInstance"
      v-if="displayGameEndView"
      :gameState="gameState"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import DemoWaitView from '@/components/demoComponents/DemoWaitView.vue';
import PromptTimerAnimation from '@/components/gameComponents/PromptTimerAnimation';
import DemoQuestionView from '@/components/demoComponents/DemoQuestionView';
import VoteStartView from '@/components/gameComponents/VoteStartView.vue';
import DemoVoteView from '@/components/demoComponents/DemoVoteView.vue';
import VoteResultView from '@/components/gameComponents/VoteResultView.vue';
import RoundResultView from '@/components/gameComponents/RoundResultView.vue';
import GameEndView from '@/components/gameComponents/GameEndView.vue';

export default Vue.extend({
  name: 'GameView',
  components: {
    DemoWaitView,
    PromptTimerAnimation,
    DemoQuestionView,
    VoteStartView,
    DemoVoteView,
    VoteResultView,
    RoundResultView,
    GameEndView
  },
  props: {
    username: String,
    gameRoomcode: String,
    gameState: Object,
    primaryColor: String,
    isHost: Boolean,
    totalSecondsRemaining: Number,
    timeRemaining: String,
  },
  data: () => ({
    displayWaitView: false,
    displayQuestionView: false,
    displayVoteStartView: false,
    displayVoteView: false,
    displayVoteResultView: false,
    displayRoundResultView: false,
    displayGameEndView: false,
    turnInfo: {
      prompt: '',
      answers: [{
        answerId: '',
        answerText: ''
      }, {
        answerId: '',
        answerText: ''
      }],
      canPlayerVote: true,
      wereAllAnswersSubmitted: true
    },
    previousTurnResult: {
      prompt: '',
      resultMsg: '',
      answers: [{
        answerId: '',
        answerText: '',
        username: '',
        primaryColor: '',
        numVotes: 0
      }, {
        answerId: '',
        answerText: '',
        username: '',
        primaryColor: '',
        numVotes: 0
      }]
    },
    activeQuestionIndex: 0,
    isSafari: false,
    // demo flags
    prevSecondsRemaining: 0,
    didTimerReset: false,
    canSkipturn: [true, true],
    round: {
      didRemote1Answer: false,
      didRemote2Answer: false,
      didRemote3Answer: false,
    },
    turn0: {
      didRemote2Vote: false,
      didRemote3Vote: false
    },
    turn1: {
      didRemote1Vote: false,
      didRemote3Vote: false
    },
    turn2: {
      didRemote2Vote: false
    },
    turn3: {
      didRemote3Vote: false
    }
  }),
  computed: {
    prompts: function () {
      if ('prompts' in this.gameState) {
        return this.gameState.prompts;
      }
      return [];
    },
    isAnswerSubmissionComplete: function () {
      if ('prompts' in this.gameState) {
        return this.activeQuestionIndex === this.gameState.prompts.length;
      }
      return false;
    },
    isLastRound: function () {
      if ('roundIndex' in this.gameState && 'numRounds' in this.gameState) {
        return this.gameState.roundIndex === this.gameState.numRounds;
      }
      return false;
    },
    scoreboard: function() {
      return 'scoreboard' in this.gameState ? this.gameState.scoreboard : [];
    }
  },
  methods: {
    updateTurnInfo () {
      if ('turnInfo' in this.gameState) {
        this.turnInfo = this.gameState.turnInfo;
      } else {
        this.turnInfo = {
          prompt: '',
          answers: [{
            answerId: '',
            answerText: ''
          }, {
            answerId: '',
            answerText: ''
          }]
        };  
      }
    },
    resetActiveQuestionIndex () {
      this.activeQuestionIndex = 0;
    },
    advanceActiveQuestionIndex () {
      ++this.activeQuestionIndex;
    },
    submitAnswer (answer) {
      this.advanceActiveQuestionIndex();
      this.$emit('submit-player-answer', answer);
    },
    submitVote (vote) {
      this.$emit('submit-player-vote', vote);
    },
    propagateEvent(eventName, eventData) {
      this.$emit(eventName, eventData);
    }
  },
  watch: {
    prompts: function () {
      this.resetActiveQuestionIndex();
    }
  },
  mounted: function() {
    this.$nextTick(function() {
      this.isSafari = navigator.userAgent.indexOf('Safari') != -1 && navigator.userAgent.indexOf('Chrome') === -1;
    });
  },
  beforeUpdate: function() {
    // so we know that the timer will usually have a difference of about 1 second, so if the difference is more than 2 seconds,
    // we know that the timer did in fact reset
    if (!this.didTimerReset) {
      this.didTimerReset = Math.abs(this.totalSecondsRemaining - this.prevSecondsRemaining) > 2;
    }
    // console.log('-----');
    // console.log('prevSecondsRemaining: ', this.prevSecondsRemaining);
    // console.log('totalSecondsRemaining: ', this.totalSecondsRemaining);
    // console.log('timeRemaining: ', this.timeRemaining);
    // console.log('didTimerReset: ', this.didTimerReset);
    // console.log('-----');
  },
  updated: function() {
    // the logic below is logic for when the 'AI' should emit certain events based on how much time is remaining
    /* minor explanation here:
      - the reason we use this flag is because the timing window was way too finicky to get perfect and would cause bugs occasionally:
      sometimes the timer doesnt fall between 80 and 81 - i'm not sure why this happens but it did. using these flags (e.g. 
      this.round.didRemote1Answer ) should be more stable.
    */

    // round 0 - everyone submits their answers 
    this.prevSecondsRemaining = this.totalSecondsRemaining
    if(this.displayQuestionView) {
      if (this.didTimerReset && !this.round.didRemote1Answer && this.totalSecondsRemaining <= 80) {
        this.round.didRemote1Answer = true;
        this.$emit('update-game-state', 'remote1HasAnswered');
      } else if (this.didTimerReset && !this.round.didRemote2Answer && this.totalSecondsRemaining <= 77) {
        this.round.didRemote2Answer = true;
        this.$emit('update-game-state', 'remote2HasAnswered');
      } else if (this.didTimerReset && !this.round.didRemote3Answer && this.totalSecondsRemaining <= 75) {
        this.round.didRemote3Answer = true;
        this.$emit('update-game-state', 'remote3HasAnswered');
      } else if (this.didTimerReset && this.totalSecondsRemaining <= 0) {
        this.$emit('update-game-state', 'timeHasRunOut');
      }
    }

    // turn 0 - player and remote ai 1 responses - remote ai 2 and remote ai 3 vote
    if(this.canSkipturn[0] && this.gameState.turnIndex == 0 && this.gameState.turnInfo && !this.gameState.turnInfo.wereAllAnswersSubmitted) {
      // console.log('player did not submit their first answer');
      this.$emit('update-game-state', 'skipTurn0');
      this.canSkipturn[0] = false;
    } else if(this.displayVoteView && this.gameState.turnIndex == 0) {
      if (this.didTimerReset && !this.turn0.didRemote2Vote && this.totalSecondsRemaining <= 25) {
        this.turn0.didRemote2Vote = true;
        this.$emit('update-game-state', 'remote2HasVoted');
      } else if (this.didTimerReset && !this.turn0.didRemote3Vote && this.totalSecondsRemaining <= 22) {
        this.turn0.didRemote3Vote = true;
        this.$emit('update-game-state', 'remote3HasVoted');
        this.$emit('update-game-state', 'goToNextStep');
      }
    }

    // turn 1 - player and remote ai 2 responses - remote ai 1 and remote ai 3 vote
    if(this.canSkipturn[1] && this.gameState.turnIndex == 1 && this.gameState.turnInfo && !this.gameState.turnInfo.wereAllAnswersSubmitted) {
      // console.log('player did not submit their second answer');
      this.$emit('update-game-state', 'skipTurn1');
      this.canSkipturn[1] = false;
    } else if(this.displayVoteView && this.gameState.turnIndex == 1) {
      if (this.didTimerReset && !this.turn1.didRemote1Vote && this.totalSecondsRemaining <= 25) {
        this.turn1.didRemote1Vote = true;
        this.$emit('update-game-state', 'remote1HasVoted');
      } else if (this.didTimerReset && !this.turn1.didRemote3Vote && this.totalSecondsRemaining <= 22) {
        this.turn1.didRemote3Vote = true;
        this.$emit('update-game-state', 'remote3HasVoted');
        this.$emit('update-game-state', 'goToNextStep');
      } 
    }
    
    // turn 2 - remote ai 1 and 3 responses - player and remote ai 2 vote
    if (this.displayVoteView && this.gameState.turnIndex == 2) {
      if (this.didTimerReset && !this.turn2.didRemote2Vote && this.totalSecondsRemaining <= 25) {
        this.turn2.didRemote2Vote = true;
        this.$emit('update-game-state', 'remote2HasVotedAgain');
      } else if (this.didTimerReset && this.totalSecondsRemaining <= 0) {
        this.$emit('update-game-state', 'timeHasRunOut2');
      }      
    }
    
    // turn 3 - remote ai 2 and 3 responses - player and remote ai 1 vote
    if (this.displayVoteView && this.gameState.turnIndex == 3) {
      if (this.didTimerReset && !this.turn3.didRemote3Vote && this.totalSecondsRemaining <= 25) {
        this.turn3.didRemote3Vote = true;
        this.$emit('update-game-state', 'remote1HasVotedAgain');
      } else if (this.didTimerReset && this.totalSecondsRemaining <= 0) {
        this.$emit('update-game-state', 'timeHasRunOut2');
      }
    }
  }
})
</script>

<style lang="scss">
@import '@/styles/game-view-styles.scss';
</style>
